import React from 'react';
import { Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { CheckoutPlan } from '../utils/checkout';

// While online payment is pending (PLAN-18; Paddle not configured), a buy button becomes this: a short note and an email
// link to ask for a free month of the plan, for the user (Student plan) or for one of the teacher's classes (Class plan).
// An email the user writes, not a button (the owner's choice, 2026-11-05): a conversation, and the owner grants the month
// in the admin dashboard (PLAN-9), which emails the user (PLAN-19). The message is prefilled with the plan and the
// account's address so the owner can find the account.
// Remove with PLAN-18 at go-live (docs/DEPLOYMENT.md, "Go-live checklist").

export const TRIAL_NOTE = "We're finishing online payments. Until then, email us and we'll give you a free month.";
export const TRIAL_EMAIL = 'contact@webots.academy';

export function trialMailto(plan: CheckoutPlan, accountEmail?: string, forClass?: { id: number; name: string }): string {
  const what = plan === 'class' ? `the Class plan${forClass ? ` for my class "${forClass.name}"` : ''}` : 'the Student plan';
  const subject = plan === 'class' ? 'Free month: Class plan' : 'Free month: Student plan';
  const body = [
    'Hi,',
    '',
    `I'd like a free month of ${what}.`,
    '',
    `My Webots Academy account: ${accountEmail ?? ''}`,
    ...(forClass ? [`Class: ${forClass.name} (#${forClass.id})`] : []),
    '',
    'Thanks!',
  ].join('\n');
  return `mailto:${TRIAL_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const TrialRequest: React.FC<{
  plan: CheckoutPlan;
  forClass?: { id: number; name: string };
  // Shown before the note: what the plan gives (the buy button said it).
  offer?: React.ReactNode;
  // `span` where it sits inside a paragraph (PlanLine).
  as?: 'div' | 'span';
  className?: string;
  noteClassName?: string;
  buttonClassName?: string;
  icon?: React.ReactNode;
}> = ({ plan, forClass, offer, as: Tag = 'div', className, noteClassName, buttonClassName, icon }) => {
  const { user } = useAuth();
  const Line = Tag === 'span' ? 'span' : 'p';
  return (
    <Tag className={className} data-testid="trial-request">
      {offer && <Line className="block">{offer}</Line>}
      <Line className={`block ${noteClassName ?? 'text-sm text-gray-600 dark:text-gray-400'}`}>{TRIAL_NOTE}</Line>
      <a href={trialMailto(plan, user?.email, forClass)} className={`${buttonClassName ?? 'btn btn-primary'} mt-2`}>
        {icon ?? <Mail className="h-4 w-4" aria-hidden="true" />} Email {TRIAL_EMAIL}
      </a>
    </Tag>
  );
};

export default TrialRequest;
